export const AGENT_HUB_TRAVELLER_RESPONSE_TYPE = 'agent_hub_traveller'

export interface AgentHubTravellerApiItem {
  id: string
  type: string
  status: string
  booking_number: string | null
  title: string | null
  dates: {
    start_date: string | Date | null
    end_date: string | Date | null
  }
  property?: {
    id: string | null
    name: string | null
  }
}

export interface AgentHubTravellerApiContact {
  name?: string | null
  agency_name?: string | null
  email?: string | null
  guidance?: string
}

export interface AgentHubTravellerApiOrder {
  response_type: typeof AGENT_HUB_TRAVELLER_RESPONSE_TYPE
  id: string
  number: string
  brand: string
  status: App.OrderStatus | string
  display_status: string
  created_at: string
  updated_at: string
  items: Array<AgentHubTravellerApiItem>
  agent_contact: AgentHubTravellerApiContact
}

const emptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined

const dateString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return value
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString()
  }
  return undefined
}

export function isAgentHubTravellerApiResponse(order: unknown): order is AgentHubTravellerApiOrder {
  return Boolean(
    order &&
    typeof order === 'object' &&
    (order as AgentHubTravellerApiOrder).response_type === AGENT_HUB_TRAVELLER_RESPONSE_TYPE,
  )
}

function mapItem(item: AgentHubTravellerApiItem): App.AgentHubTravellerItem {
  return {
    id: String(item.id),
    type: item.type,
    status: item.status,
    bookingNumber: emptyString(item.booking_number),
    title: emptyString(item.title),
    startDate: dateString(item.dates?.start_date),
    endDate: dateString(item.dates?.end_date),
    ...(item.property ? {
      property: {
        id: emptyString(item.property.id),
        name: emptyString(item.property.name),
      },
    } : {}),
  }
}

function mapContact(contact: AgentHubTravellerApiContact | undefined): App.AgentHubTravellerContact {
  if (!contact) {
    return {}
  }

  const name = emptyString(contact.name)
  const agencyName = emptyString(contact.agency_name)
  const email = emptyString(contact.email)
  const guidance = emptyString(contact.guidance)

  if (name || agencyName || email) {
    return {
      name,
      agencyName,
      email,
      ...(guidance ? { guidance } : {}),
    }
  }

  return guidance ? { guidance } : {}
}

export function agentHubTravellerOrderMap(
  order: AgentHubTravellerApiOrder,
  type: 'summary' | 'full' = 'summary',
): App.AgentHubTravellerOrder {
  return {
    responseType: AGENT_HUB_TRAVELLER_RESPONSE_TYPE,
    id: order.id,
    bookingNumber: order.number,
    brand: order.brand,
    status: order.status as App.OrderStatus,
    displayStatus: order.display_status,
    createdAt: order.created_at,
    updatedAt: order.updated_at,
    items: (order.items ?? []).map(mapItem),
    agentContact: mapContact(order.agent_contact),
    type,
    isBookingOwner: false,
    accessDetails: {
      role: 'AGENT_BOOKING_TRAVELLER',
      isSharedUser: true,
      canView: true,
      canEdit: false,
      canPayShare: false,
    },
  }
}
